import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Loading } from '@components/atoms/Loading';
import { useSubscription } from '@hooks/api/subscription/useSubscription';
import { useAddonSubscriptions } from '@hooks/api/subscription/useAddonSubscriptions';
import { useTotalIncludedSeats } from '@hooks/api/subscription/useTotalIncludedSeats';
import { useActiveSeats } from '@hooks/api/subscription/useActiveSeats';
import { usePaymentMethods } from '@hooks/api/subscription/usePaymentMethods';
import { useOrganizationTransactionHistory } from '@hooks/api/subscription/useOrganizationTransactionHistory';
import { useCancelSubscription } from '@hooks/api/subscription/useCancelSubscription';
import { usePauseSubscription } from '@hooks/api/subscription/usePauseSubscription';
import { useResumeSubscription } from '@hooks/api/subscription/useResumeSubscription';
import { useAddPaymentMethod } from '@hooks/api/subscription/useAddPaymentMethod';
import { useDeletePaymentMethod } from '@hooks/api/subscription/useDeletePaymentMethod';
import { useSetDefaultPaymentMethod } from '@hooks/api/subscription/useSetDefaultPaymentMethod';
import { useStartSubscription } from '@hooks/api/subscription/useStartSubscription';
import { useActivateSubscriptionAfterTrial } from '@hooks/api/subscription/useActivateSubscriptionAfterTrial';
import SubscriptionDetails from './components/SubscriptionDetails';
import PaymentMethods from './components/PaymentMethods';
import BillingHistory from './components/BillingHistory';
import OrganizationSeats from './components/OrganizationSeats';
import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import { CreditCard } from 'lucide-react';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useTranslation } from '@hooks/useTranslation';

const OwnerBillingPage: React.FC = () => {
  const t = useTranslation('billing');
  // Get organization name from Redux
  const organizationName = useAppSelector(
    (state: RootState) => state.organization.name,
  );
  const organizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );

  const history = useHistory();
  const location = useLocation();
  const { data: subscription, isLoading: isLoadingSubscription } =
    useSubscription();
  const { data: addonSubscriptions = [], isLoading: isLoadingAddons } =
    useAddonSubscriptions();
  const { data: totalIncludedSeats } = useTotalIncludedSeats();
  const { data: activeSeats = [] } = useActiveSeats();
  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } =
    usePaymentMethods();
  const { data: transactions = [], isLoading: isLoadingTransactions } =
    useOrganizationTransactionHistory({});

  const { mutateAsync: activateAfterTrial, isPending: isActivatingAfterTrial } =
    useActivateSubscriptionAfterTrial();
  const { mutateAsync: cancelSubscription, isPending: isCanceling } =
    useCancelSubscription();
  const { mutateAsync: pauseSubscription, isPending: isPausing } =
    usePauseSubscription();
  const { mutateAsync: resumeSubscription, isPending: isResuming } =
    useResumeSubscription();
  const { mutateAsync: addPaymentMethod, isPending: isAddingPaymentMethod } =
    useAddPaymentMethod();
  const {
    mutateAsync: deletePaymentMethod,
    isPending: isDeletingPaymentMethod,
  } = useDeletePaymentMethod();
  const { mutateAsync: setDefaultPaymentMethod, isPending: isSettingDefault } =
    useSetDefaultPaymentMethod();
  const { mutateAsync: startSubscription, isPending: isStartingSubscription } =
    useStartSubscription();

  const [showCancelModal, setShowCancelModal] = useState(false);

  // Ref for scrolling to payment methods section
  const paymentMethodsRef = useRef<HTMLDivElement>(null);

  const handlePause = async () => {
    if (!subscription) return;
    if (window.confirm(t.subscription.confirmPause)) {
      await pauseSubscription(subscription.id);
    }
  };

  const handleResume = async () => {
    if (!subscription) return;
    await resumeSubscription(subscription.id);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!subscription) return;
    await cancelSubscription({
      subscriptionId: subscription.id,
      isBaseSubscription: true,
    });
    // The modal stays open on purpose: it switches to a confirmation step that the user
    // dismisses themselves. Closing here would put them straight back on a billing page
    // that is about to be navigated away from, with nothing said about what happened.
  };

  const handleAddPaymentMethod = async (data: any) => {
    await addPaymentMethod(data);
  };

  /**
   * Turn a lapsed trial into a paying subscription using the card already on file.
   *
   * `addPaymentMethod` only vaults a card — it never touches subscription status, and its
   * NMI-sync query filters to ACTIVE/TRIAL, so a TRIAL_EXPIRED organization stayed
   * read-only no matter how many cards its owner added. This is the step that was missing.
   *
   * Uses `useExistingPaymentMethod` so the owner is not asked to retype a card the system
   * already holds; the plan is the one the trial was already on, so no plan choice is
   * needed here (`/trial-expired` remains the flow for picking a different plan).
   */
  const handleActivateAfterTrial = async () => {
    if (!subscription || !organizationId) return;

    const card =
      paymentMethods.find((method) => method.isDefault) || paymentMethods[0];
    if (!card) return;

    await activateAfterTrial({
      organizationId,
      useExistingPaymentMethod: true,
      existingPaymentMethodId: card.paymentMethodId,
      billingCycle: subscription.billingPeriod,
    });
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    await deletePaymentMethod(paymentMethodId);
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    await setDefaultPaymentMethod(paymentMethodId);
  };

  const handleStartSubscription = async () => {
    if (window.confirm(t.subscription.confirmStart)) {
      await startSubscription();
    }
  };

  const handleAddPaymentMethodForStart = () => {
    // Scroll to payment methods section and trigger add modal
    paymentMethodsRef.current?.scrollIntoView({ behavior: 'smooth' });
    // The PaymentMethods component will handle opening its own modal
  };

  /**
   * `?addPayment=1` means the user came here to pay — from the trial banner or the
   * trial-ending email — so the add-card modal opens straight away.
   *
   * Read once into state and then stripped from the URL with `replace`, so a refresh or a
   * Back navigation does not reopen the modal the user just dismissed. A query param
   * rather than router state on purpose: the email link can use it too, and router state
   * does not survive a page load.
   */
  const [autoOpenAddPayment] = useState(
    () => new URLSearchParams(location.search).get('addPayment') === '1',
  );

  useEffect(() => {
    if (autoOpenAddPayment) {
      history.replace(location.pathname);
    }
  }, [autoOpenAddPayment, history, location.pathname]);

  if (isLoadingSubscription || isLoadingPaymentMethods || isLoadingAddons) {
    return <Loading size="2xl" center />;
  }

  const isPerformingAction = isCanceling || isPausing || isResuming;

  return (
    <div className="w-full pb-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg">
          <CreditCard className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.subscription.pageTitle}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t.subscription.pageSubtitle}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        <SubscriptionDetails
          subscription={subscription}
          paymentMethods={paymentMethods}
          onPause={handlePause}
          onResume={handleResume}
          onCancel={handleCancelClick}
          onStartSubscription={handleStartSubscription}
          onAddPaymentMethodForStart={handleAddPaymentMethodForStart}
          onActivateAfterTrial={handleActivateAfterTrial}
          isActivatingAfterTrial={isActivatingAfterTrial}
          isLoading={isPerformingAction}
          isStartingSubscription={isStartingSubscription}
          totalSeats={totalIncludedSeats}
          activeSeatsCount={activeSeats.length}
        />

        <div ref={paymentMethodsRef}>
          <PaymentMethods
            autoOpenAddModal={autoOpenAddPayment}
            paymentMethods={paymentMethods}
            onAddPaymentMethod={handleAddPaymentMethod}
            onDeletePaymentMethod={handleDeletePaymentMethod}
            onSetDefaultPaymentMethod={handleSetDefaultPaymentMethod}
            isLoading={
              isAddingPaymentMethod ||
              isDeletingPaymentMethod ||
              isSettingDefault
            }
          />
        </div>

        <BillingHistory
          transactions={transactions}
          isLoading={isLoadingTransactions}
        />
        <OrganizationSeats
          baseSubscription={subscription}
          addonSubscriptions={addonSubscriptions}
          totalIncludedSeats={totalIncludedSeats}
          isLoading={isLoadingSubscription || isLoadingAddons}
        />
      </div>

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        organizationName={organizationName || t.subscription.organizationFallback}
        isLoading={isCanceling}
      />
    </div>
  );
};

export default OwnerBillingPage;
